/**
 * BF offer — the cart's safety net (drawer + cart page).
 *
 * v2 (7 Sep 2026): sofa + Stoffbezug earn Kissen-Set 1 AND the Komplettes
 * Schutzset. The sofa PDP puts both gift lines in the same /cart/add.js call
 * as the cover (kc-bf-offer.js), so this file never needs to act in the
 * normal flow. It covers everything else — carts assembled without a sofa
 * PDP, and carts that CHANGE after the fact:
 *
 *   heal    every `_bf_gift` line back to quantity 1
 *   remove  gift lines the cart no longer earns (the cover left, or the sofa)
 *   add     gifts the cart earns and lacks, from the nodes Liquid rendered
 *
 * All three are decided from ONE fresh /cart.js snapshot through KcBfRules,
 * and NEVER without it: an add or a deletion we cannot justify is the one
 * mistake with no undo. This is the only script that writes gift lines
 * outside the add-to-cart call, on every page including the PDP — v1 had two
 * writers and they fought.
 *
 * Standalone and bound at document level, because the drawer re-renders its
 * own inner HTML on every change and a script injected that way never runs
 * again.
 */
(function () {
  "use strict";

  var GIFT_PROPERTY = "_bf_gift";

  function routeRoot() {
    var shopify = window.Shopify;
    if (shopify && shopify.routes && shopify.routes.root) return shopify.routes.root;
    return "/";
  }

  /**
   * Tells BOTH cart surfaces to redraw.
   *
   * The drawer listens for `cart:refresh` (theme.js, CartDrawer). The cart
   * PAGE does not listen to it at all — it has its own <main-cart> element
   * that re-fetches the page on `mainCart:refresh` (assets/cart.js), so
   * without that second event the page kept showing the pre-add cart until
   * the customer reloaded by hand.
   *
   * `cart:change` is deliberately NOT dispatched: theme.js reads
   * `event.detail["cart"]` from it to refresh its cached cart promise, so a
   * bare one throws in the theme's own listener and leaves that cache broken.
   */
  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    document.dispatchEvent(new CustomEvent("mainCart:refresh", { bubbles: true }));
  }

  function rulesConfig() {
    var rules = window.KcBfRules;
    var configNode = document.querySelector("[data-kc-bf-rules]");
    if (!rules || !configNode) return null;
    var cfg = rules.readConfig(configNode);
    return cfg ? { rules: rules, cfg: cfg } : null;
  }

  /**
   * Adds the gifts described by {kind, variantId, handle} - ONE request for
   * all of them, so the pair lands together and counts as a single add
   * against the ceiling below.
   */
  /**
   * The Kissen-Set fabric the customer picked in this cart, if they did.
   * Liquid renders the node with the fabric matched to the sofa; a pick made
   * here wins over it for the rest of the page's life.
   */
  var chosenSetVariantId = 0;
  /** ...and the product that fabric belongs to (Kissen Set 1, or Kissen Set 8
   *  for a Nora): a pick made on one set must never be added as the other. */
  var chosenSetProductId = "";

  async function addGifts(gifts) {
    var items = [];
    for (var i = 0; i < gifts.length; i++) {
      var variantId = gifts[i].variantId;
      if (chosenSetVariantId && gifts[i].kind === "set") {
        // Unknown product on either side (stale markup): the old behaviour.
        var sameSet =
          !gifts[i].productId || !chosenSetProductId || gifts[i].productId === chosenSetProductId;
        if (sameSet) variantId = chosenSetVariantId;
      }
      if (!variantId || !gifts[i].handle) continue;
      var properties = {};
      properties[GIFT_PROPERTY] = gifts[i].handle;
      items.push({ id: variantId, quantity: 1, properties: properties });
    }
    if (!items.length) return false;

    var response = await fetch(routeRoot() + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items }),
    });
    if (!response.ok) throw new Error("gift add failed: " + response.status);

    // The cart re-render clears the nodes on its own: the Liquid conditions
    // that produced them are false once the lines exist.
    refreshCartUI();
    return true;
  }

  /**
   * Forces every gift line back to quantity 1.
   *
   * A gift line is one unit by definition — the discounts carry
   * `usesPerOrderLimit: 1`, so unit two onwards is charged in full. But two
   * theme-side adds produce IDENTICAL properties (`_bf_gift` and nothing
   * else), so Shopify MERGES them into a single line with a growing quantity
   * instead of a second line anyone would notice. A cart was found holding 160
   * "free" protection sets — 13.600 EUR — as one tidy-looking row.
   *
   * This runs before anything else on every cart event, so the cart self-heals
   * whatever produced the surplus, including code that no longer exists.
   */
  async function normalizeGiftQuantities(items, skipKeys) {
    var surplus = items.filter(function (item) {
      if (skipKeys && skipKeys[item.key]) return false;
      return item.properties && item.properties._bf_gift && item.quantity > 1;
    });
    if (!surplus.length) return false;

    for (var i = 0; i < surplus.length; i++) {
      console.warn(
        "[bf-cart-gift] gift line at quantity",
        surplus[i].quantity,
        "->",
        surplus[i].handle,
        "resetting to 1"
      );
      await fetch(routeRoot() + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: surplus[i].key, quantity: 1 }),
      });
    }
    refreshCartUI();
    return true;
  }

  async function removeLines(keys) {
    for (var i = 0; i < keys.length; i++) {
      await fetch(routeRoot() + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: keys[i], quantity: 0 }),
      });
    }
    refreshCartUI();
  }

  /**
   * The "you're missing this" button: open the cover's variant picker.
   *
   * The picker is the theme's own <x-drawer class="kc-modal">, rendered for
   * every cart-upsell product by blocks/_kc-upsell-modal.liquid, and
   * `data-upsell-source="cart"` is what tells upsell-variant-selector.js to
   * ADD the chosen variant instead of only writing it back to a card. Once
   * the cover lands, the pass below sees sofa + cover and adds the two gifts.
   *
   * Adding the cover outright would hand the customer whichever fabric came
   * first — the wrong one, most of the time — so the picker comes first and
   * the direct add is only the fallback for a page that has no picker.
   */
  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-kc-bf-cart-cover]");
    if (!button || button.hasAttribute("aria-busy")) return;

    event.preventDefault();

    var productId = button.getAttribute("data-product-id") || "";
    var modal =
      document.getElementById(button.getAttribute("data-modal-id") || "") ||
      document.querySelector('x-drawer.kc-modal[data-product-id="' + productId + '"]');

    if (modal) {
      // Remembered so the cover can be added with the fabric the customer is
      // about to choose, and the gift's own fabric offered right after it.
      var rc = rulesConfig();
      var setProductId = rc && rc.cfg.setIds.length ? rc.cfg.setIds[0] : "";
      coverChain = {
        coverKey: "upsell-item-input-" + productId,
        setKey: setProductId ? "upsell-item-input-" + setProductId : "",
        setProductId: setProductId,
        button: button,
      };

      // Deliberately NOT data-upsell-source="cart". That flag hands the add
      // to upsell-variant-selector.js, which is the drawer's own path and
      // does not fire on the cart PAGE — the picker saved and nothing landed.
      // The add is done here instead, so the cover arrives the same way from
      // every entry point.
      modal.removeAttribute("data-upsell-source");
      modal.setAttribute("open", "");
      return;
    }

    var variantId = parseInt(button.getAttribute("data-variant-id"), 10);
    if (!variantId) {
      // Nothing to add and nothing to open: the product page is better than a
      // button that does nothing.
      var url = button.getAttribute("data-product-url");
      if (url) window.location.href = url;
      return;
    }

    button.setAttribute("aria-busy", "true");
    coverChain = null;
    addCover(variantId).catch(function (error) {
      console.error("[bf-cart-gift] could not add the cover:", error);
      button.removeAttribute("aria-busy");
    });
  });

  /**
   * A CONFIGURATION's notice ("add the cover" / "your free set is missing"):
   * the CTA reopens the saved configuration in the configurator, where the
   * cover and the gifted Kissenset are added with everything the EDI needs
   * (`_cushion_data` per cushion) — nothing this script could build.
   *
   * Before navigating, this configuration's lines leave the cart: every line
   * sharing the parent's `_set_id` (pieces, covers, the configurator's own
   * gifts). The configurator's add-to-cart never replaces an existing
   * configuration, so coming back with the cover would otherwise put a
   * second sofa in the cart. The Schutzset THIS script added carries no
   * `_set_id`; with the sofa gone the next pass removes it as an orphan.
   *
   * The configuration itself is safe: it lives in the configurator's database
   * (the `configId` in the link), not in the cart. A removal that fails still
   * navigates — a duplicate the customer can see beats a dead button.
   */
  document.addEventListener("click", function (event) {
    var link = event.target.closest("[data-kc-bf-cart-config-link]");
    if (!link || link.hasAttribute("aria-busy")) return;

    var href = link.getAttribute("href");
    var setId = link.getAttribute("data-set-id") || "";
    if (!href) return;

    event.preventDefault();
    link.setAttribute("aria-busy", "true");

    removeConfiguration(setId)
      .catch(function (error) {
        console.error("[bf-cart-gift] could not remove the configuration:", error);
      })
      .then(function () {
        window.location.href = href;
      });
  });

  /** Removes every cart line whose `_set_id` is this configuration's. */
  async function removeConfiguration(setId) {
    if (!setId) return;

    var response = await fetch(routeRoot() + "cart.js", { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("cart read failed: " + response.status);

    var cart = await response.json();
    var updates = {};
    var any = false;
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._set_id === setId) {
        updates[item.key] = 0;
        any = true;
      }
    });
    if (!any) return;

    // ONE request for the whole group: /cart/change.js one line at a time
    // would re-render the drawer between calls and fire this script's own
    // upkeep pass on a half-removed configuration.
    var change = await fetch(routeRoot() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    });
    if (!change.ok) throw new Error("configuration remove failed: " + change.status);
  }

  /**
   * "Ändern" on the gifted Kissen Set's cart line: open that set's own
   * variant picker.
   *
   * `data-upsell-source` is removed deliberately. WITH it, the modal's save
   * path adds the chosen variant itself — a second, PAID set on top of the
   * gift. Without it the modal only broadcasts the pick, and the
   * `upsell:variant-changed` listener below hands it to
   * `applyChosenSetVariant()`, which removes the old gift line and re-adds
   * the chosen fabric carrying `_bf_gift`. Same path the cover notice uses.
   */
  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-kc-bf-change-set]");
    if (!button) return;

    var productId = button.getAttribute("data-product-id") || "";
    var modal = document.getElementById("upsell-modal-" + productId);
    if (!modal) return;

    event.preventDefault();
    modal.removeAttribute("data-upsell-source");
    awaitingSetKey = "upsell-item-input-" + productId;
    awaitingSetProductId = productId;
    modal.setAttribute("open", "");
  });

  /**
   * A control with no picker behind it is worse than no control.
   *
   * The picker is rendered by the theme's overlay group, not by the cart, so
   * it is normally there — but the campaign's modal resolves its product
   * through `collections[…]`/`all_products[…]`, and either can come back
   * empty (the 20-lookup cap on a busy page). The button hides itself rather
   * than becoming a dead click.
   */
  function syncChangeSetControls() {
    var controls = document.querySelectorAll("[data-kc-bf-change-set]");
    for (var i = 0; i < controls.length; i++) {
      var productId = controls[i].getAttribute("data-product-id") || "";
      controls[i].hidden = !document.getElementById("upsell-modal-" + productId);
    }
  }

  /** Set while the notice's cover picker is open, so its save can add the
   *  cover and then hand the customer the gift's fabric. */
  var coverChain = null;
  /** The reference key of the gift picker we are waiting on. */
  var awaitingSetKey = null;
  /** ...and its product id (Set 1, or a model's set such as Kissen Set 8). */
  var awaitingSetProductId = "";

  /**
   * Swaps an already-added gift line to the fabric just chosen.
   *
   * Remove AND add, both here. Removing alone and leaving the re-add to the
   * upkeep pass is what made the set vanish: Liquid only renders an autogift
   * node for a gift the cart is MISSING, and at that moment the cart still
   * had one — so by the time the line was gone there was nothing in the DOM
   * describing what to put back, and the pass had nothing to add. The fabric
   * is known right here, so the swap does not need the markup at all.
   *
   * The order matters: the stale line goes first. Adding first would leave
   * two set lines for an instant, and the duplicate rule keeps the OLDEST —
   * which is precisely the one being replaced.
   */
  async function applyChosenSetVariant() {
    var rc = rulesConfig();
    if (!rc || !chosenSetVariantId) return;

    var response = await fetch(routeRoot() + "cart.js", { headers: { Accept: "application/json" } });
    if (!response.ok) return;

    var cart = await response.json();
    // Black Weeks 2026: the gifted set is Set 1 OR a model's own set (Kissen
    // Set 8 for a Nora). A fabric picked on a model set swaps only that
    // product's gift line and keeps its `_bf_gift`; on Set 1, as before.
    var rules = rc.rules;
    var modelGift =
      chosenSetProductId && rules.modelGiftByProduct
        ? rules.modelGiftByProduct({ product_id: chosenSetProductId }, rc.cfg)
        : null;
    var stale = (cart.items || []).filter(function (item) {
      if (!item.properties || !item.properties._bf_gift) return false;
      if (!rules.isSetLine(item, rc.cfg)) return false;
      if (modelGift) {
        if (String(item.product_id) !== modelGift.productId) return false;
      } else if (rules.modelGiftByTag && rules.modelGiftByTag(item, rc.cfg)) {
        return false;
      }
      return item.variant_id !== chosenSetVariantId;
    });

    // Nothing added yet: the pass that adds the pair will pick the chosen
    // fabric up from `chosenSetVariantId` on its own.
    if (!stale.length) return;

    await removeLines(
      stale.map(function (item) {
        return item.key;
      })
    );

    await addGifts([
      {
        kind: "set",
        variantId: chosenSetVariantId,
        productId: chosenSetProductId,
        handle: modelGift ? modelGift.handle : rc.cfg.giftA,
      },
    ]);
  }

  /** Puts the chosen cover in the cart. The gifts follow from the upkeep
   *  pass, which sees sofa + cover and completes the pair. */
  async function addCover(variantId) {
    var response = await fetch(routeRoot() + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!response.ok) throw new Error("cover add failed: " + response.status);

    refreshCartUI();
  }

  document.addEventListener("upsell:variant-changed", function (event) {
    var detail = event.detail || {};
    var key = detail.referenceCheckboxId;
    if (!key) return;

    // The cover's fabric was chosen in the picker the notice opened: add it,
    // then offer the gift's fabric after the drawer's close animation, so one
    // dialog never opens over another.
    if (coverChain && key === coverChain.coverKey) {
      var chain = coverChain;
      coverChain = null;

      var variantId = parseInt(detail.variantId, 10) || 0;
      if (variantId) {
        if (chain.button) chain.button.setAttribute("aria-busy", "true");
        addCover(variantId).catch(function (error) {
          console.error("[bf-cart-gift] could not add the cover:", error);
          if (chain.button) chain.button.removeAttribute("aria-busy");
        });
      }

      if (!chain.setProductId) return;

      setTimeout(function () {
        var modal = document.getElementById("upsell-modal-" + chain.setProductId);
        if (!modal) return;
        // Deliberately NOT data-upsell-source="cart": the gift is not bought
        // here, it is swapped into the line the upkeep pass adds.
        modal.removeAttribute("data-upsell-source");
        awaitingSetKey = chain.setKey;
        awaitingSetProductId = chain.setProductId;
        modal.setAttribute("open", "");
      }, 450);
      return;
    }

    if (awaitingSetKey && key === awaitingSetKey) {
      awaitingSetKey = null;
      chosenSetVariantId = parseInt(detail.variantId, 10) || 0;
      chosenSetProductId = String(awaitingSetProductId || "");
      awaitingSetProductId = "";
      applyChosenSetVariant().catch(function (error) {
        console.error("[bf-cart-gift] could not apply the chosen fabric:", error);
      });
    }
  });

  /** A pass is in flight; a second one would duplicate every action. */
  var upkeepBusy = false;
  /** A pass asked for while one was in flight, and whether it may add. */
  var upkeepQueued = false;
  var upkeepQueuedAdds = false;

  /**
   * Hard ceiling on automatic adds per page load.
   *
   * Not a substitute for the checks below — a circuit breaker behind them. Any
   * future add/remove disagreement between this script and another writer
   * would otherwise spend the customer's cart one unit at a time, and the
   * evidence says that is not hypothetical.
   */
  var autogiftAdds = 0;
  // The pair, plus the one re-add a fabric change costs.
  var MAX_AUTOGIFT_ADDS = 3;

  /**
   * Which gift lines the cart no longer earns — computed from the cart itself.
   *
   * ONLY lines carrying `_bf_gift`: a set the customer BOUGHT is theirs, and
   * removing it because they dropped the cover would delete something they
   * paid for. Returns [] without the rules, because a deletion we cannot
   * justify is the one mistake with no undo.
   *
   * Set 2 is a retired gift (v1). KcBfRules answers "never earned" for it, so
   * any `_bf_gift` Set 2 line still around is an orphan and goes.
   */
  function findOrphans(items, codes) {
    var rc = rulesConfig();
    if (!rc) return [];
    var rules = rc.rules;
    var cfg = rc.cfg;
    var status = rules.giftStatus(items, cfg, codes);

    // One line of each gift is earned, never two.
    var kept = {};

    return items.filter(function (item) {
      if (!item.properties || !item.properties._bf_gift) return false;

      var kind;
      var earned;
      if (rules.isSchutzsetLine(item, cfg)) {
        kind = "schutzset";
        earned = status.schutzset.eligible;
      } else if (rules.isSetBLine && rules.isSetBLine(item, cfg)) {
        kind = "setB";
        earned = status.setB ? status.setB.eligible : status.set.eligible;
      } else {
        kind = "set";
        earned = status.set.eligible;
        // Black Weeks 2026, Nora: the cart earns ONE set, Kissen Set 8 for a
        // Nora and Set 1 otherwise (status.set.handle). A `_bf_gift` set of
        // the other kind is stale — the Nora left, or a Set 1 gift landed in
        // a Nora cart — and goes; the add pass then brings the right one. A
        // stale cached rules file has no matchesSetGift: old behaviour.
        if (earned && rules.matchesSetGift && !rules.matchesSetGift(item, status, cfg)) {
          earned = false;
        }
      }

      if (!earned) {
        rules.debug("orphan:", item.handle, item.key);
        return true;
      }

      // A SECOND line of a gift the cart has already been given. It happens
      // when the offer is taken from two sofa PDPs in different fabrics: two
      // adds with identical properties merge into one line (and the quantity
      // heal above puts it back to 1), but two different variants stay two
      // lines — and the discount only ever zeroes one of them, so the second
      // would be charged in full. The oldest line stays.
      if (kept[kind]) {
        rules.debug("duplicate gift:", item.handle, item.key);
        return true;
      }
      kept[kind] = true;
      return false;
    });
  }

  /**
   * What the two gifts are, in this cart, according to the markup.
   *
   * The autogift nodes are the first source. The NOTICE is the second, and it
   * is there because of the cart PAGE: it does not always re-render its
   * Liquid between the cover landing and the pass below, so those nodes can
   * still be the ones from the render before - that is, absent - while the
   * notice (rendered while the cart still had no cover) is right there
   * carrying the same two products. The drawer, which does re-render, uses
   * the first source.
   */
  function giftCandidates() {
    var nodes = document.querySelectorAll("[data-kc-bf-cart-autogift]");
    if (nodes.length) {
      return Array.prototype.map.call(nodes, function (node) {
        return {
          kind: node.getAttribute("data-gift-kind") || "schutzset",
          variantId: parseInt(node.getAttribute("data-variant-id"), 10),
          handle: node.getAttribute("data-gift-handle"),
          productId: node.getAttribute("data-product-id") || "",
        };
      });
    }

    var notice = document.querySelector("[data-kc-bf-cart-missing]");
    if (!notice) return [];

    return [
      {
        kind: "set",
        variantId: parseInt(notice.getAttribute("data-bf-set-variant-id"), 10),
        handle: notice.getAttribute("data-bf-set-handle"),
      },
      {
        kind: "schutzset",
        variantId: parseInt(notice.getAttribute("data-bf-schutzset-variant-id"), 10),
        handle: notice.getAttribute("data-bf-schutzset-handle"),
      },
    ];
  }

  /**
   * Of those, the ones the FETCHED cart really lacks. Whichever source the
   * markup came from, THIS is the guard that keeps a gift from being handed
   * out twice: the cart in hand has the final say, per gift kind.
   */
  function pendingGifts(items, codes) {
    var candidates = giftCandidates();
    if (!candidates.length) return [];

    var rc = rulesConfig();
    // Without the rules we cannot tell whether one is already there, and an
    // add we can't justify is exactly how a cart reaches 160 of them.
    if (!rc) return [];

    var status = rc.rules.giftStatus(items, rc.cfg, codes);
    rc.rules.debug("autogift check:", status);

    // A solo sofa (Nora): its gifts are the 3-in-1 set, which costs more than the sofa alone, so they only come
    // with the set chosen on the PDP. A Nora bought on its own ("Nur Sofa") gets nothing added here.
    if (status.earnedBy === "solo") return [];

    return candidates.filter(function (gift) {
      if (!gift.variantId || !gift.handle) return false;
      var state = status[gift.kind];
      if (!(state && state.pending)) return false;
      // Black Weeks 2026, Nora: only the set this cart earns (Kissen Set 8
      // for a Nora, Set 1 otherwise). Markup a render behind — the notice's
      // Set 1 next to a Nora — must not add the other one. A status without
      // `handle` (stale rules file) skips the check: old behaviour.
      if (gift.kind === "set" && state.handle && gift.handle !== state.handle) {
        rc.rules.debug("autogift skipped, not this cart's set:", gift.handle, state.handle);
        return false;
      }
      return true;
    });
  }

  /**
   * One pass over the cart: heal, remove, then add — all three decided from a
   * SINGLE /cart.js snapshot.
   *
   * The ORDER matters:
   *
   *  1. Quantities first, so a surplus is never mistaken for anything else —
   *     except on lines that are about to go, which are skipped rather than
   *     reset to 1 and then deleted.
   *  2. Removals next, and the pass STOPS after one. The cart it just changed
   *     is no longer the cart in `items`, and adding on stale data is exactly
   *     how a gift once got handed out twice.
   *  3. Only a cart that needed nothing removed and nothing healed is calm
   *     enough to add to.
   */
  async function upkeep(allowAdds) {
    var response = await fetch(routeRoot() + "cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;

    var cart = await response.json();
    var items = cart.items || [];
    // the codes applied to THIS snapshot: one of the campaign's gift codes
    // earns the gifts next to a cover without a sofa (kc-bf-rules.js). A
    // stale cached rules file has no appliedCodes: then no codes, old rule.
    var rcCodes = rulesConfig();
    var codes = rcCodes && rcCodes.rules.appliedCodes ? rcCodes.rules.appliedCodes(cart) : [];

    var orphans = findOrphans(items, codes);
    var orphanKeys = {};
    orphans.forEach(function (item) {
      orphanKeys[item.key] = true;
    });

    var healed = await normalizeGiftQuantities(items, orphanKeys);

    if (orphans.length) {
      await removeLines(
        orphans.map(function (item) {
          return item.key;
        })
      );
      return;
    }

    if (healed || !allowAdds) return;

    var pending = pendingGifts(items, codes);
    if (!pending.length) return;
    if (autogiftAdds >= MAX_AUTOGIFT_ADDS) {
      console.warn("[bf-cart-gift] automatic add ceiling reached, standing down");
      return;
    }

    autogiftAdds++;
    await addGifts(pending);
  }

  /**
   * Serialises upkeep. A pass requested mid-flight is not dropped but QUEUED —
   * dropping it is what would let the fast removal pass swallow the slower add
   * pass behind it — and the two collapse into one trailing run.
   */
  function runUpkeep(allowAdds) {
    if (upkeepBusy) {
      upkeepQueued = true;
      if (allowAdds) upkeepQueuedAdds = true;
      return;
    }

    upkeepBusy = true;
    upkeep(allowAdds)
      .catch(function (error) {
        console.error("[bf-cart-gift] upkeep failed:", error);
      })
      .then(releaseUpkeep, releaseUpkeep);
  }

  function releaseUpkeep() {
    // A settle before the next pass: /cart.js read immediately after a write
    // can still answer with the pre-write cart, and acting on that is how a
    // second gift gets added.
    setTimeout(function () {
      upkeepBusy = false;
      if (!upkeepQueued) return;
      var adds = upkeepQueuedAdds;
      upkeepQueued = false;
      upkeepQueuedAdds = false;
      runUpkeep(adds);
    }, 400);
  }

  // Three events: the theme's add-to-cart dispatches cart:change, cart:refresh
  // is the "reread everything" signal, and mainCart:refresh is what the /cart
  // page's own writers fire (assets/cart.js: the discount form after a code is
  // applied or removed, RemoveSet, the bfcache restore). Without the third a
  // gift code typed into the cart earned the gifts and nothing added them
  // until the next reload (20 Oct 2026).
  //
  // TWO passes, because the two halves wait on different things:
  //
  //  - Removing and healing read a fetched /cart.js, which is true the moment
  //    the write returns. They run early. Waiting made an orphaned gift
  //    visibly linger while the customer watched the drawer.
  //  - The add reads Liquid nodes, and the drawer holds its innerHTML swap
  //    for 1250ms on any event that isn't `variant:add`. Reading at 250ms
  //    would find the PREVIOUS render — usually no node at all, so a missing
  //    gift would silently never be added.
  ["cart:change", "cart:refresh", "mainCart:refresh"].forEach(function (name) {
    document.addEventListener(name, function () {
      setTimeout(function () {
        runUpkeep(false);
        syncChangeSetControls();
      }, 250);
      setTimeout(function () {
        runUpkeep(true);
        syncChangeSetControls();
      }, 1400);
      // mainCart:refresh only ANNOUNCES a re-fetch of the cart's sections; the
      // swap lands whenever the network does, and on a slow connection (or the
      // theme-dev proxy) it was still the old markup at 1400ms, so the gift
      // nodes a code had just earned were never read. One more look, later.
      // Every pass re-verifies against a fresh /cart.js, so a pass that runs
      // twice adds nothing twice.
      if (name === "mainCart:refresh") {
        setTimeout(function () {
          runUpkeep(true);
          syncChangeSetControls();
        }, 3600);
      }
    });
  });

  function upkeepOnLoad() {
    runUpkeep(true);
    syncChangeSetControls();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", upkeepOnLoad);
  } else {
    upkeepOnLoad();
  }
})();
